<script>
import useSharedData from "@/use/SharedData";
import useTools from "@/use/Tools";
import axios from "axios";
import {computed, onMounted, ref} from "vue";
import useSharedMethod from "@/use/SharedMethod";
import {useRecruitmentRequestStore} from "@/stores/Request/RecruitmentRequest";

export default {
    props: {
        updateAction: Boolean,
        viewAction: Boolean,
        lockAction: Boolean,
    },
    setup(props) {
        const {dialog, loading, overlay} = useSharedData();
        const {$route} = useTools();
        const {formatedTime, formatedDate, getBack} = useSharedMethod();

        const candidates = ref([]);

        const recruitmentRequestStore = useRecruitmentRequestStore();

        const readonly = computed(() => props.viewAction || props.lockAction);

        const rootPath = computed(() => {
            if (props.updateAction) {
                return 'recruitment_decisions.edit';
            } else if (props.viewAction) {
                return 'recruitment_decisions.view'
            } else if (props.lockAction) {
                return 'recruitment_decisions.lock';
            }
            return null;
        })

        function findRecap() {
            loading.value = true;
            axios.get(`/recruitmentRequests/recap/${$route.params.id}`).then((response) => {
                candidates.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function selectCandidate(closed) {
            loading.value = true;
            axios.put(`/recruitmentRequests/selectCandidate`, candidates.value, {
                params: {
                    id: $route.params.id,
                    closed: closed
                }
            }).then((response) => {
                recruitmentRequestStore.updateRecruitmentRequest(response.data);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => findRecap());

        return {
            dialog,
            loading,
            overlay,
            candidates,
            rootPath,
            readonly,
            formatedTime,
            formatedDate,
            getBack,
            selectCandidate,
        }
    }
}
</script>

<template>
    <div>
        <v-dialog
            v-model="dialog"
            max-width="1500"
            persistent
            scrollable
        >
            <v-card>
                <w-toolbar
                    v-if="updateAction"
                    color="deep-orange"
                    :title="$t('common.decision')"
                />
                <w-dialog-toolbar
                    v-else
                    :view-action="viewAction"
                    :lock-action="lockAction"
                />
                <w-overlay
                    :loading="loading"
                    :overlay="overlay"
                    :lock-action="lockAction"
                    @lock="selectCandidate(true)"
                    @cancel="overlay = false"
                />
                <v-card-text>
                    <v-container fluid>
                        <v-simple-table
                            v-if="candidates.length > 0"
                            dense
                        >
                            <template v-slot:default>
                                <thead>
                                <tr>
                                    <th class="text-left" colspan="5" style="background-color: #FFE0B2"></th>
                                    <th
                                        class="text-center"
                                        v-for="(item, index) in candidates[0].elements"
                                        :key="`header-${index}`"
                                        colspan="3"
                                        style="background-color: #E1BEE7;"
                                    >
                                        {{ item.typeName }}
                                    </th>
                                </tr>
                                <tr>
                                    <th class="text-left">{{ $t('common.last_name') }}</th>
                                    <th class="text-left">{{ $t('common.first_name') }}</th>
                                    <th class="text-center">{{ $t('recruitment_request.cv') }}</th>
                                    <th class="text-center">{{ $t('recruitment_request.selection') }}</th>
                                    <th class="text-center">{{ $t('common.observation') }}</th>

                                    <template v-for="(item, index) in candidates[0].elements">
                                        <th :key="`header-note-${index}`">{{ $t('common.note') }}</th>
                                        <th :key="`header-decision-${index}`">{{ $t('common.decision') }}</th>
                                        <th class="text-center" :key="`header-interview-${index}`">{{ $t('recruitment_request.interview') }}</th>
                                    </template>

                                </tr>
                                </thead>
                                <tbody>
                                <tr
                                    v-for="(item, index) in candidates"
                                    :key="`item-${index}`"
                                >
                                    <td>{{ item.lastName }}</td>
                                    <td>{{ item.firstName }}</td>
                                    <td class="text-center">
                                        <w-btn-icon
                                            color="blue darken-2"
                                            icon="mdi-cube-scan"
                                            @press="$router.push({name: `${rootPath}.cv_techs.view`, params: {id: item.cvTechId}})"
                                        />
                                    </td>
                                    <td
                                        class="text-center"
                                    >
                                        <v-checkbox
                                            v-model="item.selected"
                                            :readonly="readonly"
                                            class="mt-n2"
                                            hide-details
                                        />
                                    </td>
                                    <td class="text-center">
                                        <span v-if="readonly">{{ item.description }}</span>
                                        <v-edit-dialog
                                            v-else
                                            :return-value.sync="item.description"
                                            large
                                            persistent
                                        >
                                            {{ item.description }}
                                            <v-btn icon small>
                                                <v-icon small color="orange">mdi-pencil</v-icon>
                                            </v-btn>
                                            <template v-slot:input>
                                                <v-container fluid>
                                                    <w-simple-text-area
                                                        v-model="item.description"
                                                        :label="$t('common.observation')"
                                                    />
                                                </v-container>
                                            </template>
                                        </v-edit-dialog>
                                    </td>
                                    <template v-for="(element, indey) in item.elements">
                                        <td :key="`element-note-${index}-${indey}`">
                                            <v-chip small color="teal" outlined v-if="element.interviewNote && element.done">
                                                <strong>{{ element.interviewNote.toFixed(2) }}</strong>
                                            </v-chip>
                                        </td>
                                        <td :key="`element-decision-${index}-${indey}`">
                                            <v-chip label small v-if="element.interviewDecision && element.done">
                                                <strong>{{ $t(`recruitment_request.evaluation_decision_${element.interviewDecision}`) }}</strong>
                                            </v-chip>
                                        </td>
                                        <td
                                            :key="`element-interview-${index}-${indey}`"
                                            class="text-center"
                                        >
                                            <v-btn
                                                v-if="element.done"
                                                icon
                                                color="blue darken -2"
                                                @click="$router.push({name: `${rootPath}.interview.view`, params: {id: element.id}});"
                                            >
                                                <v-icon>mdi-clipboard-text-clock</v-icon>
                                            </v-btn>
                                        </td>
                                    </template>
                                </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn
                        v-if="updateAction"
                        icon="mdi-arrow-decision"
                        color="deep-orange"
                        :text="$t('recruitment_request.decide')"
                        :loading="loading"
                        @press="selectCandidate(false)"
                    />
                    <w-btn-lock
                        :show="lockAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
        <router-view/>
    </div>
</template>

<style scoped>
table, th, td {
    border-right: 1px solid black;
}
</style>